import { useQuery } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";

export type AuditLogEntry = {
  id: string;
  actor_user_id: string | null;
  actor_email: string | null;
  accion: string;
  entidad: string | null;
  entidad_id: string | null;
  payload_antes: Record<string, unknown> | null;
  payload_despues: Record<string, unknown> | null;
  correlation_id: string | null;
  created_at: string;
  entidad_label: string | null;
  entidad_link: string | null;
};

export type PagedAudit = {
  items: AuditLogEntry[];
  total: number;
  page: number;
  size: number;
  pages: number;
};

export type AuditFilters = {
  entidad?: string;
  entidad_id?: string;
  actor_user_id?: string;
  accion?: string;
  fecha_dia?: string | null;  // YYYY-MM-DD, null = sin filtro
  q?: string;
  page?: number;
  size?: number;
};

function buildSearchParams(filters: AuditFilters): Record<string, string | number> {
  const p: Record<string, string | number> = {
    page: filters.page ?? 1,
    size: filters.size ?? 50,
  };
  if (filters.entidad) p.entidad = filters.entidad;
  if (filters.entidad_id) p.entidad_id = filters.entidad_id;
  if (filters.actor_user_id) p.actor_user_id = filters.actor_user_id;
  if (filters.accion) p.accion = filters.accion;
  if (filters.fecha_dia) p.fecha_dia = filters.fecha_dia;
  if (filters.q && filters.q.trim()) p.q = filters.q.trim();
  return p;
}

/**
 * Hook para timeline de entidad específica (cliente/contrato/etc).
 * Solo dispara con entidad_id o filtros explícitos — evita cargar todo por error.
 */
export function useAuditLog(filters: AuditFilters) {
  return useQuery({
    queryKey: ["audit-log", filters],
    queryFn: () =>
      apiCall<PagedAudit>("get", "/api/v1/audit-log", {
        searchParams: buildSearchParams(filters),
      }),
    enabled: !!(
      filters.entidad_id || filters.actor_user_id || filters.accion || filters.q
    ),
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook para la página global /auditoria. Siempre habilitado (lista todo el
 * audit, con los filtros provistos — que pueden estar vacíos).
 */
export function useAuditGlobal(filters: AuditFilters) {
  return useQuery({
    queryKey: ["audit-log-global", filters],
    queryFn: () =>
      apiCall<PagedAudit>("get", "/api/v1/audit-log", {
        searchParams: buildSearchParams(filters),
      }),
    placeholderData: (prev) => prev,
  });
}

export function useAuditAcciones() {
  return useQuery({
    queryKey: ["audit-log-acciones"],
    queryFn: () => apiCall<string[]>("get", "/api/v1/audit-log/acciones"),
    staleTime: 60_000,
  });
}

export type AuditActor = { id: string; email: string };

export function useAuditActores() {
  return useQuery({
    queryKey: ["audit-log-actores"],
    queryFn: () => apiCall<AuditActor[]>("get", "/api/v1/audit-log/actores"),
    staleTime: 60_000,
  });
}
